/**
 * 업로드 압축의 크기·형식 판단(순수 함수) — [compressImage] 가 쓰고, 단위 테스트(tests/imageEncodePlan.test.ts)가 직접 부른다.
 * 브라우저 API 를 쓰지 않으므로 node --test 로 그대로 실행된다.
 */

/** 이 폭을 넘는 이미지만 줄인다 */
export const DOWNSCALE_MIN_WIDTH: number = 2000;
/** 줄일 때 가로·세로에 곱하는 비율 */
export const DOWNSCALE_RATIO: number = 0.7;
/**
 * WebP 형식의 가로·세로 한계. 크롬·안드로이드 `toBlob('image/webp')` 는 이걸 넘으면 오류 없이 **아래를 잘라** 돌려준다
 * (1080×20000 → 1080×16383 확인). 그래서 넘으면 WebP 를 만들지 않고 원본을 올린다(2026-10-19 사용자 결정).
 */
export const WEBP_MAX_DIMENSION: number = 16383;

export type ImageSize = { width: number; height: number };

/** 인코딩할 크기 — 폭이 [DOWNSCALE_MIN_WIDTH] 를 넘으면 가로·세로를 [DOWNSCALE_RATIO] 로, 아니면 그대로 */
export const targetSize = (width: number, height: number): ImageSize =>
    width > DOWNSCALE_MIN_WIDTH
        ? { width: Math.round(width * DOWNSCALE_RATIO), height: Math.round(height * DOWNSCALE_RATIO) }
        : { width, height };

/** 가로·세로 중 하나라도 WebP 한계를 넘는지 */
export const exceedsWebpLimit = ({ width, height }: ImageSize): boolean =>
    width > WEBP_MAX_DIMENSION || height > WEBP_MAX_DIMENSION;

export type EncodePlan = ImageSize & { output: 'image/jpeg' | 'image/webp' };

/**
 * 원본 형식·크기로 인코딩 계획을 정한다. null 이면 원본을 그대로 올린다.
 * - GIF·지원하지 않는 형식: null(애니메이션이 깨진다)
 * - JPEG: JPEG 그대로(JPEG 한계 65,535 라 16,383 판단 없음)
 * - PNG·WebP: WebP. 줄인 뒤 크기가 [WEBP_MAX_DIMENSION] 을 넘으면 null
 */
export const planEncode = (fileType: string, width: number, height: number): EncodePlan | null => {
    if (!width || !height) return null;
    const size: ImageSize = targetSize(width, height);
    if (fileType === 'image/jpeg') return { ...size, output: 'image/jpeg' };
    if (fileType !== 'image/png' && fileType !== 'image/webp') return null;
    if (exceedsWebpLimit(size)) return null;
    return { ...size, output: 'image/webp' };
};
